/**
 * The login step machine, in one place (F-033).
 *
 * The server names the screen that comes next (`nextStep`). This maps the
 * names the app knows onto a screen and onto the gate `loginStepGuard` reads,
 * and answers `null` for anything else — a step added to the backend later, a
 * typo, an empty or missing value, or a `COMPLETE` on a call that cannot
 * complete a sign-in. Callers refuse a `null` step; they never guess one.
 *
 * The effects (which route to take) and the reducer (how far to open the
 * gate) both read this file, so the two can no longer drift apart.
 */
export type LoginStep =
  | 'CREDENTIALS'
  | 'VERIFY_OTP'
  | 'GOOGLE_AUTH'
  | 'GOOGLE_AUTH_SETUP';

export function parseLoginStep(step: unknown): LoginStep | null {
  if (typeof step !== 'string') {
    return null;
  }
  switch (step.toUpperCase()) {
    case 'CREDENTIALS':
      return 'CREDENTIALS';
    case 'OTP':
    case 'VERIFY_OTP':
      return 'VERIFY_OTP';
    case 'GOOGLE_AUTH':
    // user-management's own name for the authenticator step (AuthStepEnum)
    case 'VERIFY_2FA':
      return 'GOOGLE_AUTH';
    // the login rule asks for Google Authenticator and the member has none:
    // they enrol on the same screen before the session opens
    case 'GOOGLE_AUTH_SETUP':
    case 'SETUP_2FA':
      return 'GOOGLE_AUTH_SETUP';
    default:
      return null;
  }
}

/** The `minStep` each screen is guarded with in `app.routes.ts`. */
export const LOGIN_STEP_GATE: Record<LoginStep, number> = {
  CREDENTIALS: 1,
  VERIFY_OTP: 2,
  GOOGLE_AUTH: 3,
  GOOGLE_AUTH_SETUP: 3,
};

/** Both Google Authenticator steps open the same screen. */
export function isGoogleAuthStep(step: LoginStep | null): boolean {
  return step === 'GOOGLE_AUTH' || step === 'GOOGLE_AUTH_SETUP';
}

export const LOGIN_STEP_ROUTE: Record<LoginStep, string[]> = {
  CREDENTIALS: ['/auth', 'login', 'credentials'],
  VERIFY_OTP: ['/auth', 'login', 'verify-otp'],
  GOOGLE_AUTH: ['/auth', 'login', 'google-auth'],
  GOOGLE_AUTH_SETUP: ['/auth', 'login', 'google-auth'],
};
